import { useMutation } from '@tanstack/react-query'
import { useQueryCallbacks } from 'core/hooks/data/useQueryCallbacks'
import APIService from 'api/APIService'
import { downloadFile } from 'utils/AppHelper'
import { JobStatus, PayPeriod } from 'types/entity'
import Button from 'core/components/buttons/Button'
import { useSettings } from 'core/hooks/settings/useSettings'
import useNotifications from 'core/hooks/ui/useNotifications'

const useDownloadPeriodSummary = (onSuccess?: VoidFunction) => {
  const { data: settings, set } = useSettings()
  const notification = useNotifications()

  const jobID = settings.pollingJobs.downloadSummaryJobId

  const {
    mutate: downloadSummary,
    status: summaryStatus,
    isPending: downloadingSummary
  } = useMutation({
    mutationFn: ({
      periods,
      includeDetails
    }: {
      periods: PayPeriod['id'][]
      includeDetails: boolean
    }) => {
      return APIService.payPeriods.payPeriodSummaryDownload({
        pay_period_ids: periods,
        include_trips: includeDetails,
        include_transactions: includeDetails
      })
    },
    onSuccess: (data) => {
      set('pollingJobs', {
        ...settings.pollingJobs,
        downloadSummaryJobId: data.token_id
      })
      onSuccess?.()
    },
    onError: () => {
      notification.error('An error has occurred trying to download this file')
    }
  })

  const loadingNotificationId = `download-summary-${jobID}`

  useQueryCallbacks({
    queryKey: ['download-summary', jobID],
    queryFn: () =>
      APIService.misc.getJobStatus({
        jobId: jobID
      }),
    enabled: !!jobID,
    refetchInterval: 2000,
    onSuccess: (response: JobStatus) => {
      const errors = response?.error_list ?? []
      const hasErrors = errors.length > 0
      if (response.status === 'finished' && !hasErrors) {
        set('pollingJobs', {
          ...settings.pollingJobs,
          downloadSummaryJobId: ''
        })
        downloadFile(response.data.file_url as string)
        notification.success({
          title: 'Your download is complete.'
        })

        notification.dismiss(loadingNotificationId)
      }

      if (
        (response.status === 'in_progress' || response.status === 'enqueued') &&
        !hasErrors
      ) {
        notification.loading({
          id: loadingNotificationId,
          position: 'bottom-left',
          title: 'Downloading summary...',
          subtitle: 'Downloading summary, this may take a few minutes.',
          duration: Infinity
        })
      }

      if (
        response.status === 'failed' ||
        (response.status === 'finished' && hasErrors)
      ) {
        notification.dismiss(loadingNotificationId)
        notification.error({
          title: 'There were some errors with your file',
          subtitle: (
            <div>
              {typeof response.data?.message === 'string' &&
                response.data.message?.split(';').map((error) => {
                  return <span key={error}>{error.replace(';', '')}</span>
                })}
            </div>
          ),
          id: jobID,
          footer: () => {
            return (
              <Button
                className="text-primary"
                variant="link"
                onClick={() => {
                  if (response.data.file_url)
                    downloadFile(response.data.file_url as string)
                }}
              >
                Download
              </Button>
            )
          }
        })
        set('pollingJobs', {
          ...settings.pollingJobs,
          downloadSummaryJobId: ''
        })
      }
    },
    onError: () => {
      notification.dismiss(loadingNotificationId)
      notification.error({
        title: 'Could not download summary',
        id: jobID
      })
      set('pollingJobs', {
        ...settings.pollingJobs,
        downloadSummaryJobId: ''
      })
    }
  })

  return {
    downloadSummary,
    summaryStatus,
    downloadingSummary
  }
}

export default useDownloadPeriodSummary
